import React, { createContext, useState, useEffect, useContext, useRef, useMemo } from 'react';
// Employee domain
import { getEmployees, saveEmployees } from '../services/firebase/employeeFirebaseService.ts';
// Team domain
import { getTeams, saveTeams } from '../services/firebase/teamFirebaseService.ts';
// Budget domain
import { getBudgetData } from '../services/firebase/budgetFirebaseService.ts';
import { normalizeEmployeeId } from '../utils/employeeIdGenerator.ts';
import { AppError, ErrorCodes } from '../utils/errorHandler.ts';
import { useAuth } from './AuthContext.tsx';

// 🔧 DEBUG_MODE 설정 - 카테고리별 디버그 로그 제어
// 사용법: 필요한 카테고리를 true로 설정하여 해당 디버그 로그만 활성화
// 예: TEAM_CALCULATION: true로 설정하면 팀별 목표매출 계산 관련 로그만 출력
const DEBUG_MODE = {
  EMPLOYEE_MANAGEMENT: false,    // 직원 관리 관련 로그 (직원 추가/삭제)
  TEAM_CALCULATION: true,        // 팀별 목표매출 계산 로그 (인건비율, 목표매출 계산) - 미지정 팀 매출 합산 디버깅용
  DATA_LOADING: false,            // 데이터 로드 관련 로그 (Firebase 데이터 읽기) - 팀 데이터 로드 확인용
  ACTUAL_SALES_REFRESH: false,   // 실제매출 자동화 새로고침 로그
  TEAM_PERFORMANCE_REFRESH: true, // 팀 성과 관리 새로고침 로그
  DATA_SAVE: false,              // 데이터 저장 관련 로그 (Firebase 데이터 저장)
  ERROR_HANDLING: false,          // 에러 처리 로그 (try-catch 에러) - 팀 관련 에러 확인용
  TEAM_NORMALIZATION: false,      // 팀 데이터 정규화 로그 (중복 제거, 이름 정규화) - 미지정 팀 처리 확인용
  ALL: false                     // 전체 디버그 로그 제어 (true 시 모든 로그 활성화)
};

// 🔧 완전한 깊은 복사 헬퍼 함수
const deepCopyMonthlyData = (monthlyData: any): any => {
  if (!monthlyData) return {};
  
  // JSON 방식으로 완전한 깊은 복사
  return JSON.parse(JSON.stringify(monthlyData));
};

// 🔧 디버그 로그 함수들
const debugLog = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.log(...args);
  }
};

// Unused debug function - commented out to fix build warning
// const debugWarn = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
//   if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
//     console.warn(...args);
//   }
// };

const debugError = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.error(...args);
  }
};

// 인센티브 산출 매출 계산을 위한 타입 정의
export interface TeamIncentiveCalculation {
  exceededSales: number;           // 기존 초과매출
  deductionAmount: number;         // 차감분 (미달성분의 50% 분배)
  incentiveCalculationSales: number; // 인센티브 산출 매출 (초과매출 - 차감분)
}

// 팀 성과 데이터 타입 정의
export interface TeamPerformanceData {
  teamName: string;
  targetSales: number;
  actualSales: number;
  achievementRate: number;
  incentiveAmount: number;
  exceededSales: number;           // 초과매출 (목표 달성 시)
  underperformanceSales: number;   // 미달성매출 (목표 미달 시)
  deductionAmount: number;         // 차감분
  incentiveCalculationSales: number; // 인센티브 산출 매출
  // 근무일/일평균 통계 (v2)
  workingDays?: number;            // 팀별 근무일수
  dailyAverageSales?: number;      // 팀별 일평균 매출
}

// 직원 데이터 타입 정의
export interface Employee {
  // id 필드를 필수로 변경 (정규화된 숫자 ID)
  id: number;
  name: string;
  team: string;
  position: string;
  salary: number;
  tax: number;
  netSalary: number;
  incentive: number;
  familyCount: number;
  retirement: number;
  meal: number;
  housing: number;
  overtime: number;
  overtimeMinutes?: number; // 연장근로시간 (분 단위)
  individual: number;
  other: number;
  nonTaxable: number;

  // 🆕 연차 관리 관련 필드
  hireDate?: Date | string;         // 입사일 (연차 계산 기준 - 입사 기념일)
  status?: 'active' | 'inactive';   // 재직 상태 (연차 발생 대상 여부)
  annualLeaveSettlement?: number;   // 연차 정산액 (퇴사 시 미사용 연차 금액)

  // 🔐 사용자 계정 연결을 위한 필드 (Option B)
  userId?: string;                   // Firebase Auth UID - 사용자 계정과 연결

  // 실장 전용 필드
  directorIncentiveRate?: number;              // 실장 인센티브 선공제 비율 (%, 예: 10)
  teamCostAllocation?: Record<string, number>; // 실장 인건비 팀별 배분 비율 (%, 합계 100)
}

// 예산 데이터 타입 정의
export interface BudgetData {
  targetSales: number;
  currentPersonnelBudget: number;
  personnelPercentage: number;
  actualSales: number;
  incentiveRatios: {
    performance: number; 
    team: number; 
    member: number; 
  };
  teamTargetSales: Record<string, number>;
  teamIncentiveRatios: Record<string, {
    performance: number; 
    team: number; 
    member: number; 
  }>;
  // 월별 데이터를 위한 추가 필드
  monthlyData?: Record<string, {
    // 🔧 필수 필드들 (타입 안전성 보장)
    targetSales: number;
    actualSales: number;
    personnelPercentage?: number;
    // 🔧 팀 관련 필수 필드들
    teamTargetSales?: Record<string, number>;
    teamPersonnelPercentages?: Record<string, number>;
    teamSalaryTotals?: Record<string, number>; // 팀별 인건비 합계
    teamPureTargetSales?: Record<string, number>; // 팀별 순수 목표매출
    teamActualSales?: Record<string, number>; // 팀별 실제매출
    // 🔧 직원 및 팀 목록
    employees?: Employee[];
    teams?: string[]; // 해당 월의 팀 목록
    // 🔧 인센티브 관련 필드들
    teamIncentiveRatios?: Record<string, {
      performance: number; 
      team: number; 
      member: number; 
    }>; // 월별 팀 인센티브 비율
    teamIncentiveCalculations?: Record<string, TeamIncentiveCalculation>; // 팀별 인센티브 산출 매출 계산 결과
    teamPerformanceData?: Record<string, TeamPerformanceData>; // 팀별 성과 데이터
    // 🔧 조정 및 설정 관련
    applyPreviousMonthAdjustment?: boolean; // 지난달 미달성 조정 적용 여부
    underperformanceDeductionRate?: number; // 월별 미달성팀 차감 비율
    applyOvertimeMultiplier?: boolean; // 연장근로 150% 가산 적용 여부
    // 🔧 메타데이터
    importedFromMonth?: string;
    // 🎯 진료의사-팀 매핑 정보
    doctorTeamMapping?: Record<string, string>;
    lastUpdated?: string;
    dataSource?: string; // 데이터 소스 정보
    totalActualSales?: number; // 전체 실제 매출
    calculatedAt?: string; // 계산 시간
    dataVersion?: string; // 데이터 버전 (실시간 계산과 구분)
  }>;
  // 팀별 인건비율 저장 필드
  teamPersonnelPercentages: Record<string, number>;
  // 미달성팀 차감 비율 (기본값: 50%)
  underperformanceDeductionRate: number;
  // 팀별 순수 목표매출 (지난달 미달성 조정 전)
  teamPureTargetSales?: Record<string, number>;
  // 팀별 인건비 합계
  teamSalaryTotals?: Record<string, number>;
  // 팀별 인센티브 계산 결과
  teamIncentiveCalculations?: Record<string, TeamIncentiveCalculation>;
  // 팀별 성과 데이터
  teamPerformanceData?: Record<string, TeamPerformanceData>;
}

// 팀 데이터 타입 정의
export interface TeamData {
  id: string;
  name: string;
  targetSales: number;
}

// AppContext 타입 정의
interface AppContextType {
  employees: Employee[];
  teams: TeamData[];
  budgetData: BudgetData;
  isLoading: boolean;
  dataLoadError: string | null;
  lastSaveTime: string;
  deletedEmployeeNames: string[]; // 삭제된 직원 이름 목록 추가
  saveEmployeeData: () => Promise<void>;
  saveTeamData: (teamsToSave?: TeamData[]) => Promise<void>;
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>;
  setTeams: React.Dispatch<React.SetStateAction<TeamData[]>>;
  setBudgetData: React.Dispatch<React.SetStateAction<BudgetData>>;
  addDeletedEmployeeName: (name: string) => void; // 삭제된 직원 이름 추가 함수
  clearDeletedEmployeeNames: () => void; // 삭제된 직원 이름 목록 초기화 함수
  loadEmployees: () => Promise<void>; // 직원 데이터 재로드 함수 (직원-사용자 연결용)
  // 🆕 실제매출 자동화 새로고침 관련
  registerActualSalesRefresh: (refreshFn: (targetMonth?: string) => Promise<void>) => void;
  triggerActualSalesRefreshAfterExcelUpload: (uploadedMonth?: string) => Promise<void>;
  isActualSalesRefreshing: boolean;
  // 🆕 팀 성과 관리 새로고침 관련
  registerTeamPerformanceRefresh: (refreshFn: () => Promise<void>) => void;
  triggerTeamPerformanceRefreshAfterExcelUpload: (uploadedMonth?: string) => Promise<void>;
  isTeamPerformanceRefreshing: boolean;
}

// Context 생성
const AppContext = createContext<AppContextType | undefined>(undefined);

// Context Provider 컴포넌트
export const AppProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  // 🔧 FIX: 인증 상태 확인 - 인증 완료 전 Firestore 쿼리 방지
  const { user, loading: authLoading } = useAuth();

  // 상태 설정
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [deletedEmployeeNames, setDeletedEmployeeNames] = useState<string[]>([]); // 삭제된 직원 이름 목록
  const [budgetData, setBudgetData] = useState<BudgetData>({
    targetSales: 0,
    currentPersonnelBudget: 0,
    personnelPercentage: 19,
    actualSales: 0,
    incentiveRatios: {
      performance: 10,
      team: 70,
      member: 30
    },
    teamTargetSales: {},
    teamIncentiveRatios: {},
    monthlyData: {},
    teamPersonnelPercentages: {},
    underperformanceDeductionRate: 50
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  const [lastSaveTime, setLastSaveTime] = useState<string>('');
  
  // 🆕 실제매출 자동화 새로고침 관련 상태 및 함수
  const [refreshActualSales, setRefreshActualSales] = useState<((targetMonth?: string) => Promise<void>) | null>(null);
  const [isActualSalesRefreshing, setIsActualSalesRefreshing] = useState<boolean>(false);

  // 🔧 FIX: 실제매출 새로고침 함수의 stale closure 방지를 위한 ref
  const employeesRef = useRef(employees);
  employeesRef.current = employees;

  const budgetDataRef = useRef(budgetData);
  budgetDataRef.current = budgetData;

  // 🆕 팀 성과 관리 새로고침 관련 상태
  const [refreshTeamPerformance, setRefreshTeamPerformance] = useState<(() => Promise<void>) | null>(null);
  const [isTeamPerformanceRefreshing, setIsTeamPerformanceRefreshing] = useState<boolean>(false);
  
  // 삭제된 직원 이름 추가 함수
  const addDeletedEmployeeName = (name: string) => {
    if (name && !deletedEmployeeNames.includes(name)) {
      setDeletedEmployeeNames(prev => [...prev, name]);
      debugLog('EMPLOYEE_MANAGEMENT', `[AppContext] 직원 ${name} 삭제 예정 목록에 추가됨`);
    }
  };
  
  // 삭제된 직원 이름 목록 초기화 함수
  const clearDeletedEmployeeNames = () => {
    if (deletedEmployeeNames.length > 0) {
      debugLog('EMPLOYEE_MANAGEMENT', `[AppContext] 삭제된 직원 이름 목록 초기화 (${deletedEmployeeNames.length}개)`);
      setDeletedEmployeeNames([]);
    }
  };

  // 마지막 저장 시간 포맷팅 함수
  const getFormattedTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
  };

  // (제거됨) updateTeamTargetSalesOnEmployeeChange — 전역 마스터 기반 비정본 인라인 합산(실장 배분
  //  미계상·19% 폴백)으로 root teamTargetSales/teamPersonnelPercentages/targetSales만 갱신(monthlyData
  //  미기록)하여 월별 정합을 깨던 함수. 소비자는 전부 월별-우선 리졸버를 쓰므로 삭제. (코드리뷰 BUG4)

  // 초기 데이터 로드 - 인증 완료 후 실행
  useEffect(() => {
    // 🔧 FIX: 인증이 완료되지 않았으면 데이터 로드 대기
    if (authLoading) {
      debugLog('DATA_LOADING', '인증 로딩 중 - 데이터 로드 대기');
      return;
    }

    // 🔧 FIX: 로그인되지 않은 상태면 데이터 로드 건너뜀
    if (!user) {
      debugLog('DATA_LOADING', '사용자 미인증 - 데이터 로드 건너뜀');
      setIsLoading(false);
      return;
    }

    const loadInitialData = async () => {
      try {
        setIsLoading(true);

        debugLog('DATA_LOADING', '[AppContext] 데이터 로드 시작 (인증 완료, user:', user?.email, ')');

        // 병렬 처리로 성능 최적화 (개별 오류 처리)
        setDataLoadError(null);
        const results = await Promise.allSettled([
          getEmployees(),
          getTeams(),
          getBudgetData()
        ]);

        // 개별 결과 추출 (실패 시 null로 처리, 성공한 데이터는 정상 사용)
        const loadErrors: string[] = [];
        const rawEmployeesData = results[0].status === 'fulfilled' ? results[0].value : (() => {
          loadErrors.push(`직원 데이터: ${(results[0] as PromiseRejectedResult).reason?.message || '로드 실패'}`);
          return null;
        })();
        const rawTeamsData = results[1].status === 'fulfilled' ? results[1].value : (() => {
          loadErrors.push(`팀 데이터: ${(results[1] as PromiseRejectedResult).reason?.message || '로드 실패'}`);
          return null;
        })();
        const budgetDataFromDb = results[2].status === 'fulfilled' ? results[2].value : (() => {
          loadErrors.push(`예산 데이터: ${(results[2] as PromiseRejectedResult).reason?.message || '로드 실패'}`);
          return null;
        })();

        if (loadErrors.length > 0) {
          const errorMsg = loadErrors.join('; ');
          console.error('[AppContext] 데이터 로드 실패:', errorMsg);
          // 개별 에러 상세 출력
          results.forEach((r, i) => {
            if (r.status === 'rejected') {
              console.error(`[AppContext] ${['employees', 'teams', 'budget'][i]} 에러:`, r.reason);
            }
          });
          setDataLoadError(errorMsg);
        }

        console.log('[AppContext] 데이터 로드 결과:', {
          employees: results[0].status,
          teams: results[1].status,
          budget: results[2].status
        });
        
        // 직원 데이터 처리
        if (rawEmployeesData && rawEmployeesData.length > 0) {
          const validatedEmployees = rawEmployeesData.map((emp: any, index: number) => ({
            // id 필드를 필수로 설정 (기존 id가 있으면 사용, 없으면 index 기반으로 생성)
            id: emp.id || (index + 1),
            // name은 문서 ID로 사용되며 필수 값
            name: emp.name || '',
            team: emp.team || '',
            position: emp.position || '',
            salary: Number(emp.salary) || 0,
            tax: Number(emp.tax) || 0,
            netSalary: Number(emp.netSalary) || 0,
            incentive: Number(emp.incentive) || 0,
            familyCount: Number(emp.familyCount) || 0,
            retirement: Number(emp.retirement) || 0,
            meal: Number(emp.meal) || 0,
            housing: Number(emp.housing) || 0,
            overtime: Number(emp.overtime) || 0,
            individual: Number(emp.individual) || 0,
            other: Number(emp.other) || 0,
            nonTaxable: Number(emp.nonTaxable) || 0,
            // 🔐 연차 관리 및 사용자 연결 관련 필드
            hireDate: emp.hireDate,
            status: emp.status || 'active',
            annualLeaveSettlement: Number(emp.annualLeaveSettlement) || 0,
            userId: emp.userId || undefined, // Firebase Auth UID
            directorIncentiveRate: emp.directorIncentiveRate !== undefined ? Number(emp.directorIncentiveRate) : undefined,
            teamCostAllocation: emp.teamCostAllocation || undefined,
          }));
          setEmployees(validatedEmployees);
          debugLog('DATA_LOADING', '직원 데이터 로드 완료', validatedEmployees.length);
        } else {
          debugLog('DATA_LOADING', '유효한 직원 데이터 없음');
          setEmployees([]); // 빈 배열로 명시적 설정
        }
        
        // 팀 데이터 처리
        debugLog('DATA_LOADING', '원본 팀 데이터', rawTeamsData);
        
        if (rawTeamsData && Array.isArray(rawTeamsData) && rawTeamsData.length > 0) {
          try {
            // 팀 데이터 유효성 검사 및 기본값 설정
            const validatedTeams = rawTeamsData
              .filter((team: any) => team && typeof team === 'object')
              .map((team: any) => ({
                id: team.id || '',
                name: team.name ? normalizeTeamName(team.name) : '', // 이름 정규화 적용
                targetSales: Number(team.targetSales) || 0,
              }))
              .filter(team => team.name); // 이름이 없는 팀은 제외
            
            debugLog('DATA_LOADING', '유효성 검사 후 팀 데이터', validatedTeams);
            
            // 중복 제거 처리
            const normalizedTeams = deduplicateTeams(validatedTeams);
            debugLog('DATA_LOADING', '중복 제거 후 팀 데이터', normalizedTeams);
            
            // 유효한 데이터만 설정
            setTeams(normalizedTeams);
            
            // 팀별 목표매출 설정 (정규화된 팀 이름 사용)
            const teamTargetSales: Record<string, number> = {};
            const teamNames = new Set<string>(); // 중복 확인용 Set
            
            normalizedTeams.forEach(team => {
              if (team && typeof team === 'object' && team.name) {
                const normalizedName = normalizeTeamName(team.name);
                if (!teamNames.has(normalizedName.toLowerCase())) {
                  teamNames.add(normalizedName.toLowerCase());
                  teamTargetSales[normalizedName] = team.targetSales || 0;
                } else {
                  debugLog('DATA_LOADING', `중복된 팀 이름 감지: ${normalizedName}`);
                }
              }
            });
            
            debugLog('DATA_LOADING', '정규화된 팀 목표매출', teamTargetSales);

            // 예산 데이터 처리
            debugLog('DATA_LOADING', '원본 예산 데이터', budgetDataFromDb);
            
            if (budgetDataFromDb) {
              // 팀 인센티브 비율 정규화
              const teamIncentiveRatios: Record<string, any> = {};
              
              // 기존 인센티브 비율 데이터 처리 (Firebase에 저장된 모든 팀 데이터 보존)
              if (budgetDataFromDb.teamIncentiveRatios) {
                Object.keys(budgetDataFromDb.teamIncentiveRatios).forEach(teamName => {
                  const normalizedName = normalizeTeamName(teamName);
                  if (normalizedName) {
                    teamIncentiveRatios[normalizedName] = budgetDataFromDb.teamIncentiveRatios[teamName];
                  }
                });
              }
              
              // 누락된 팀에 대한 기본 인센티브 비율 설정
              normalizedTeams.forEach(team => {
                const normalizedName = normalizeTeamName(team.name);
                if (normalizedName && !teamIncentiveRatios[normalizedName]) {
                  teamIncentiveRatios[normalizedName] = {
                    performance: budgetDataFromDb.incentiveRatios?.performance || 10,
                    team: budgetDataFromDb.incentiveRatios?.team || 70,
                    member: budgetDataFromDb.incentiveRatios?.member || 30
                  };
                }
              });
              
              // 팀 목표매출 정규화
              const normalizedTeamTargetSales: Record<string, number> = {};
              
              if (budgetDataFromDb.teamTargetSales) {
                Object.keys(budgetDataFromDb.teamTargetSales).forEach(teamName => {
                  const normalizedName = normalizeTeamName(teamName);
                  if (normalizedName && teamNames.has(normalizedName.toLowerCase())) {
                    normalizedTeamTargetSales[normalizedName] = budgetDataFromDb.teamTargetSales[teamName];
                  }
                });
              }
              
              // 정규화된 예산 데이터 설정
              setBudgetData({
                targetSales: budgetDataFromDb.targetSales || 0,
                currentPersonnelBudget: budgetDataFromDb.currentPersonnelBudget || 0,
                personnelPercentage: budgetDataFromDb.personnelPercentage || 19,
                actualSales: budgetDataFromDb.actualSales || 0,
                incentiveRatios: budgetDataFromDb.incentiveRatios || {
                  performance: 10,
                  team: 70,
                  member: 30
                },
                teamTargetSales: Object.keys(normalizedTeamTargetSales).length > 0 
                  ? normalizedTeamTargetSales 
                  : teamTargetSales,
                teamIncentiveRatios: teamIncentiveRatios,
                monthlyData: budgetDataFromDb.monthlyData || {},
                teamPersonnelPercentages: budgetDataFromDb.teamPersonnelPercentages || {},
                underperformanceDeductionRate: budgetDataFromDb.underperformanceDeductionRate || 50
              });
              
              debugLog('DATA_LOADING', '정규화된 예산 데이터 설정 완료');
            } else {
              debugLog('DATA_LOADING', '예산 데이터 없음, 기본값 사용');
              // 예산 데이터가 없는 경우 기본값 설정
              setBudgetData({
                targetSales: 0,
                currentPersonnelBudget: 0,
                personnelPercentage: 19,
                actualSales: 0,
                incentiveRatios: {
                  performance: 10,
                  team: 70,
                  member: 30
                },
                teamTargetSales: teamTargetSales,
                teamIncentiveRatios: {},
                monthlyData: {},
                teamPersonnelPercentages: {},
                underperformanceDeductionRate: 50
              });
            }
          } catch (err) {
            debugError('ERROR_HANDLING', '팀 데이터 처리 중 오류:', err);
            debugLog('DATA_LOADING', '팀 데이터 처리 오류', err);
            // 기본값으로 설정
            setTeams([]);
            // 예산 데이터도 기본값으로 설정
            setBudgetData({
              targetSales: 0,
              currentPersonnelBudget: 0,
              personnelPercentage: 19,
              actualSales: 0,
              incentiveRatios: {
                performance: 10,
                team: 70,
                member: 30
              },
              teamTargetSales: {},
              teamIncentiveRatios: {},
              monthlyData: {},
              teamPersonnelPercentages: {},
              underperformanceDeductionRate: 50
            });
          }
        } else {
          debugLog('DATA_LOADING', '유효한 팀 데이터 없음');
          setTeams([]); // 빈 배열로 명시적 설정
          // 예산 데이터 기본값 설정
          setBudgetData(prev => ({
            ...prev,
            teamTargetSales: {},
            teamIncentiveRatios: {},
            underperformanceDeductionRate: 50
          }));
        }
        
        // 예산 데이터가 아직 설정되지 않은 경우 기본값으로 설정
        if (!budgetDataFromDb) {
          debugLog('DATA_LOADING', '예산 데이터 기본값 설정');
          setBudgetData({
            targetSales: 0,
            currentPersonnelBudget: 0,
            personnelPercentage: 19,
            actualSales: 0,
            incentiveRatios: {
              performance: 10,
              team: 70,
              member: 30
            },
            teamTargetSales: {},
            teamIncentiveRatios: {},
            monthlyData: {},
            teamPersonnelPercentages: {},
            underperformanceDeductionRate: 50
          });
        }
      } catch (error: any) {
        const errorMsg = error?.message || '알 수 없는 오류';
        console.error('[AppContext] 데이터 로드 중 예외 발생:', error);
        setDataLoadError(errorMsg);
      } finally {
        setIsLoading(false);
        debugLog('DATA_LOADING', '데이터 로드 완료');
      }
    };

    loadInitialData();
  }, [authLoading, user]); // 🔧 FIX: 인증 상태 변경 시 데이터 재로드

  // 🆕 앱 초기화 완료 후 실제매출 자동화 새로고침 함수 자동 등록
  useEffect(() => {
    // 앱 초기화 완료 후 실제매출 새로고침 함수 자동 등록
    if (!isLoading && employees.length > 0 && !refreshActualSales) {
      const autoRegisterActualSalesRefresh = async () => {
        try {
          debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 앱 시작 시 실제매출 새로고침 함수 자동 등록 시작');
          
          // ActualSalesService 동적 import
          const { ActualSalesService } = await import('../services/actualSalesService.ts');
          
          const autoRefreshFunction = async (targetMonth?: string) => {
            const yearMonth = targetMonth || new Date().toISOString().substring(0, 7);
            debugLog('ACTUAL_SALES_REFRESH', `[AppContext] 자동 등록된 실제매출 새로고침 함수 실행 (${yearMonth})`);
            try {
              // 🔧 FIX: ref.current로 최신 employees/budgetData 사용 (stale closure 방지)
              const currentEmployees = employeesRef.current;
              const currentBudgetData = budgetDataRef.current;
              debugLog('ACTUAL_SALES_REFRESH', `[AppContext] 실제매출 계산 시작 — 직원: ${currentEmployees.length}명`);

              const actualSalesResult = await ActualSalesService.calculateMonthlyActualSales(
                yearMonth,
                currentEmployees,
                currentBudgetData
              );

              // 🔧 FIX-03: 상태 업데이트 (순수 함수) - 사이드 이펙트 제거
              setBudgetData(prevBudgetData => {
                // 🔧 monthlyData 깊은 복사 보장
                const deepCopiedMonthlyData = deepCopyMonthlyData(prevBudgetData.monthlyData);

                return {
                  ...prevBudgetData,
                  monthlyData: {
                    ...deepCopiedMonthlyData,
                    [yearMonth]: {
                      ...prevBudgetData.monthlyData?.[yearMonth],
                      actualSales: actualSalesResult
                    }
                  }
                } as BudgetData;
              });

              // 🔧 FIX-03: 사이드 이펙트는 상태 업데이트 후 별도 실행
              try {
                // 🛡️ 직접 saveBudgetMonthReplace 금지 — saveActualSalesData 경유로
                //    오귀속 가드(매핑부재+미지정매출 시 캐시 저장 스킵)·6필드 캐시 스키마 공유
                await ActualSalesService.saveActualSalesData(
                  yearMonth,
                  actualSalesResult,
                  budgetDataRef.current,
                  setBudgetData
                );
              } catch (saveError) {
                debugError('ACTUAL_SALES_REFRESH', '[AppContext] 자동 저장 실패:', saveError);
              }

              debugLog('ACTUAL_SALES_REFRESH', `[AppContext] 자동 등록된 실제매출 새로고침 완료 (${yearMonth})`);
            } catch (error) {
              debugError('ACTUAL_SALES_REFRESH', `[AppContext] 자동 등록된 실제매출 새로고침 실패:`, error);
              throw error;
            }
          };
          
          // 새로고침 함수 등록
          registerActualSalesRefresh(autoRefreshFunction);
          debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 앱 시작 시 실제매출 새로고침 함수 자동 등록 완료');
          
        } catch (error) {
          debugError('ACTUAL_SALES_REFRESH', '[AppContext] 앱 시작 시 실제매출 새로고침 함수 자동 등록 실패:', error);
        }
      };
      
      // 🔧 FIX: 즉시 등록 (isLoading 조건이 초기화 완료를 보장하므로 setTimeout 불필요)
      autoRegisterActualSalesRefresh();
    }
  }, [isLoading, employees.length, refreshActualSales]);

  // 직원 데이터 저장 함수
  const saveEmployeeData = async () => {
    try {
      // 삭제된 직원 이름 목록과 함께 저장
      await saveEmployees(employees, deletedEmployeeNames);
      setLastSaveTime(getFormattedTime());
      
      // 저장 성공 후 삭제된 직원 이름 목록 초기화
      if (deletedEmployeeNames.length > 0) {
        clearDeletedEmployeeNames();
      }
      
      return Promise.resolve();
    } catch (error) {
      debugError('DATA_SAVE', '직원 데이터 저장 실패:', error);
      return Promise.reject(error);
    }
  };

  // 팀 이름 데이터 정규화 함수
  const normalizeTeamName = (name: string): string => {
    return name ? name.trim() : '';
  };

  // 팀 데이터 중복 제거 함수
  const deduplicateTeams = (teamsList: TeamData[]): TeamData[] => {
    if (!teamsList || !Array.isArray(teamsList)) return [];
    
    // 유효한 팀 데이터만 필터링
    const validTeams = teamsList.filter(team => 
      team && typeof team === 'object' && team.name && typeof team.name === 'string'
    );
    
    // 중복 제거를 위한 맵 생성
    const uniqueTeams = new Map<string, TeamData>();
    
    validTeams.forEach(team => {
      const normalizedName = normalizeTeamName(team.name).toLowerCase();
      
      if (normalizedName && !uniqueTeams.has(normalizedName)) {
        // 정규화된 이름으로 업데이트
        uniqueTeams.set(normalizedName, {
          ...team,
          name: normalizeTeamName(team.name)
        });
      }
    });
    
    return Array.from(uniqueTeams.values());
  };

  // 팀 데이터 저장 함수 — 명시 인자(최신 팀 배열) 우선, 미전달 시 클로저 teams 폴백
  //    (클로저 teams는 stale할 수 있어 신규 팀 저장 누락·setTeams 롤백 위험)
  const saveTeamData = async (teamsToSave?: TeamData[]) => {
    try {
      // 저장 전 팀 데이터 중복 제거 및 정규화
      const normalizedTeams = deduplicateTeams(teamsToSave ?? teams);
      
      // 정규화된 팀 데이터로 업데이트
      setTeams(normalizedTeams);
      
      // 파이어베이스에 저장
      await saveTeams(normalizedTeams);
      setLastSaveTime(getFormattedTime());
      
      return Promise.resolve();
    } catch (error) {
      debugError('DATA_SAVE', '팀 데이터 저장 실패:', error);
      return Promise.reject(error);
    }
  };

  // (제거됨) saveBudgetInfo·saveAllData 통저장 경로 — per-month 저장 API로 전환 완료 후 무호출 확인, 코드리뷰 R3 #8·#15

  // (제거됨) setEmployeesWithTargetUpdate — 전역 마스터 setter가 root 목표매출을 비정본 재계산으로
  //  오염시키던 래퍼. 월별 목표매출 정본 쓰기 경로는 internalSaveAllData(calculateTeamSalaryTotal +
  //  현재월 root 동기화)가 유일. (코드리뷰 BUG4)


  // 🆕 실제매출 자동화 새로고침 함수 등록
  const registerActualSalesRefresh = (refreshFn: (targetMonth?: string) => Promise<void>) => {
    setRefreshActualSales(() => refreshFn);
    debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 실제매출 자동화 새로고침 함수 등록됨');
  };
  
  // 🆕 일계표 엑셀 업로드 후 실제매출 자동화 새로고침 실행
  const triggerActualSalesRefreshAfterExcelUpload = async (uploadedMonth?: string) => {
    if (refreshActualSales && !isActualSalesRefreshing) {
      try {
        setIsActualSalesRefreshing(true);
        debugLog('ACTUAL_SALES_REFRESH', `[AppContext] 일계표 엑셀 업로드 후 실제매출 자동화 새로고침 실행${uploadedMonth ? ` (${uploadedMonth}월)` : ''}`);
        
        await refreshActualSales(uploadedMonth);
        
        debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 실제매출 자동화 새로고침 완료');
      } catch (error) {
        debugError('ACTUAL_SALES_REFRESH', '[AppContext] 실제매출 자동화 새로고침 실패:', error);
      } finally {
        setIsActualSalesRefreshing(false);
      }
    } else if (!refreshActualSales) {
      debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 실제매출 자동화 새로고침 함수가 등록되지 않음');
    } else {
      debugLog('ACTUAL_SALES_REFRESH', '[AppContext] 실제매출 자동화 새로고침이 이미 진행 중');
    }
  };
  
  // 🆕 팀 성과 관리 새로고침 함수 등록
  const registerTeamPerformanceRefresh = (refreshFn: () => Promise<void>) => {
    setRefreshTeamPerformance(() => refreshFn);
    debugLog('TEAM_PERFORMANCE_REFRESH', '[AppContext] 팀 성과 관리 새로고침 함수 등록됨');
  };

  // 🔐 직원 데이터 재로드 함수 (직원-사용자 연결 후 사용)
  const loadEmployees = async () => {
    try {
      debugLog('DATA_LOADING', '[AppContext] 직원 데이터 재로드 시작');

      const rawEmployeesData = await getEmployees();

      if (rawEmployeesData && rawEmployeesData.length > 0) {
        // 새 ID가 필요한 직원에게 순차 할당하기 위한 카운터.
        // Promise.all 안에서 동시에 getNextEmployeeId를 호출하면 모두 같은 max를 읽어
        // 중복 ID가 생기므로, base를 한 번만 계산한 뒤 동기 증가(nextNewId++)로 순차 할당한다.
        const needsNewId = (e: any): boolean => {
          if (!e.id) return true;
          try { normalizeEmployeeId(e.id); return false; } catch { return true; }
        };
        // 🔒 로컬 max 기반: Firestore orderBy('id','desc')는 문자열 id가 숫자 위로 정렬되는
        // 타입-정렬 함정이 있어(레거시 문자열 id가 진짜 숫자 max를 가림 → 기존 직원과 ID 충돌),
        // 방금 읽은 전체 목록에서 정규화 가능한 최대 id를 직접 계산한다.
        // (네트워크 재조회 제거로 getNextEmployeeId 실패 모드도 이 경로에서 사라짐)
        let nextNewId = 0;
        if (rawEmployeesData.some(needsNewId)) {
          const maxExistingId = rawEmployeesData.reduce((max: number, e: any) => {
            try { return Math.max(max, normalizeEmployeeId(e.id)); } catch { return max; }
          }, 0);
          nextNewId = maxExistingId + 1;
        }

        const validatedEmployees = await Promise.all(rawEmployeesData.map(async (emp: any, index: number) => {
          let employeeId: number;

          // ID 정규화 처리
          if (emp.id) {
            // 기존 ID가 있으면 정규화
            try {
              employeeId = normalizeEmployeeId(emp.id);
              debugLog('DATA_LOADING', `[AppContext] ${emp.name}: ID 정규화 성공 - ${emp.id} → ${employeeId}`);
            } catch (error) {
              // 정규화 실패 시 새 ID 할당
              console.warn(`[AppContext] ${emp.name}: ID 정규화 실패, 새 ID 할당 중`, error);
              employeeId = nextNewId++;
              debugLog('DATA_LOADING', `[AppContext] ${emp.name}: 새 ID 할당 - ${employeeId}`);
            }
          } else {
            // ID가 없으면 새로 할당
            employeeId = nextNewId++;
            debugLog('DATA_LOADING', `[AppContext] ${emp.name}: ID 없음, 새 ID 할당 - ${employeeId}`);
          }

          return {
            id: employeeId,  // 항상 number 타입
            name: emp.name || '',
            team: emp.team || '',
            position: emp.position || '',
            salary: Number(emp.salary) || 0,
            tax: Number(emp.tax) || 0,
            netSalary: Number(emp.netSalary) || 0,
            incentive: Number(emp.incentive) || 0,
            familyCount: Number(emp.familyCount) || 0,
            retirement: Number(emp.retirement) || 0,
            meal: Number(emp.meal) || 0,
            housing: Number(emp.housing) || 0,
            overtime: Number(emp.overtime) || 0,
            individual: Number(emp.individual) || 0,
            other: Number(emp.other) || 0,
            nonTaxable: Number(emp.nonTaxable) || 0,
            // 연차 관리 및 사용자 연결 관련 필드
            hireDate: emp.hireDate,
            status: emp.status || 'active',
            annualLeaveSettlement: Number(emp.annualLeaveSettlement) || 0,
            userId: emp.userId || undefined, // Firebase Auth UID
            directorIncentiveRate: emp.directorIncentiveRate !== undefined ? Number(emp.directorIncentiveRate) : undefined,
            teamCostAllocation: emp.teamCostAllocation || undefined,
          };
        }));
        setEmployees(validatedEmployees);
        debugLog('DATA_LOADING', `[AppContext] 직원 데이터 재로드 완료: ${validatedEmployees.length}명`);
      } else {
        debugLog('DATA_LOADING', '[AppContext] 유효한 직원 데이터 없음');
        setEmployees([]);
      }
    } catch (error) {
      debugError('DATA_LOADING', '[AppContext] 직원 데이터 재로드 실패:', error);
      throw error;
    }
  };
  
  // 🆕 일계표 엑셀 업로드 후 팀 성과 관리 새로고침 실행
  const triggerTeamPerformanceRefreshAfterExcelUpload = async (uploadedMonth?: string) => {
    if (refreshTeamPerformance && !isTeamPerformanceRefreshing) {
      try {
        setIsTeamPerformanceRefreshing(true);
        debugLog('TEAM_PERFORMANCE_REFRESH', `[AppContext] 일계표 엑셀 업로드 후 팀 성과 관리 새로고침 실행${uploadedMonth ? ` (${uploadedMonth}월)` : ''}`);
        
        await refreshTeamPerformance();
        
        debugLog('TEAM_PERFORMANCE_REFRESH', '[AppContext] 팀 성과 관리 새로고침 완료');
      } catch (error) {
        debugError('TEAM_PERFORMANCE_REFRESH', '[AppContext] 팀 성과 관리 새로고침 실패:', error);
      } finally {
        setIsTeamPerformanceRefreshing(false);
      }
    } else if (!refreshTeamPerformance) {
      debugLog('TEAM_PERFORMANCE_REFRESH', '[AppContext] 팀 성과 관리 새로고침 함수가 등록되지 않음');
    } else {
      debugLog('TEAM_PERFORMANCE_REFRESH', '[AppContext] 팀 성과 관리 새로고침이 이미 진행 중');
    }
  };
  
  // 🔧 FIX-04: useMemo로 Context value 메모이제이션 — 상태 값 변경 시에만 재생성
  const contextValue = useMemo<AppContextType>(() => ({
    employees,
    teams,
    budgetData,
    isLoading,
    dataLoadError,
    lastSaveTime,
    deletedEmployeeNames,
    saveEmployeeData,
    saveTeamData,
    setEmployees, // 원시 setter — 비정본 root 목표매출 recompute 래퍼 제거 (코드리뷰 BUG4)
    setTeams,
    setBudgetData,
    addDeletedEmployeeName,
    clearDeletedEmployeeNames,
    loadEmployees, // 🔐 직원 데이터 재로드 함수
    // 🆕 실제매출 자동화 새로고침 관련 함수들
    registerActualSalesRefresh,
    triggerActualSalesRefreshAfterExcelUpload,
    isActualSalesRefreshing,
    // 🆕 팀 성과 관리 새로고침 관련 함수들
    registerTeamPerformanceRefresh,
    triggerTeamPerformanceRefreshAfterExcelUpload,
    isTeamPerformanceRefreshing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    employees, teams, budgetData, isLoading, dataLoadError, lastSaveTime,
    deletedEmployeeNames, isActualSalesRefreshing, isTeamPerformanceRefreshing
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// 커스텀 훅
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new AppError(
      'useAppContext는 AppProvider 내부에서만 사용할 수 있습니다',
      ErrorCodes.VALIDATION_ERROR,
      { hook: 'useAppContext' }
    );
  }
  return context;
};

export default AppContext; 